<template>
    <div class="mt-6 content">
        <h3 class="has-text-white"><i class="fad fa-fw fa-calendar-circle-user mr-3"></i>Sessions & Schedule</h3>

        <p class="mt-6 has-text-white" v-if="!registration.id"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading conference...</p>

        <div v-else class="mt-6 pb-6">
            <b-message type="is-warning" class="mb-6" v-if="registration.is_po_pending">
                <strong>Please Note: </strong>
                You opted to use a Purchase Order (P.O.) for your registration. Your registration will not be complete until the purchase order process is complete. Sessions and other content might not be available until the process is complete. Please contact the event organizer if you have any questions.
                <div class="mt-5 mb-4">
                    <router-link :to="{name: 'po-request'}">
                        <b-button type="is-warning" style="color:white;"><i class="fad fa-file-invoice mr-3"></i>Manage Purchase Order (P.O.)</b-button>
                    </router-link>
                </div>
            </b-message>
            <ui-card>
                <b-tabs class="schedule-tabs" animation="none" v-model="tabIndex" v-if="sessionCount > 0">

                    <b-tab-item>
                        <template #header><i class="fad fa-calendar-circle-user mr-2"></i><span class="is-hidden-mobile">Your&nbsp;</span>Schedule</template>
                        <div v-for="day in sessionsByDay" class="mt-6" v-if="hasAllowedSessions(day)">
                            <h3 class="is-hidden-mobile"><i class="fad fa-calendar-day mr-4 ml-5 has-text-primary"></i>{{ day.date | moment('dddd, MMMM Do, YYYY') }}</h3>
                            <h3 class="is-hidden-desktop"><i class="fad fa-calendar-day mr-4 ml-5 has-text-primary"></i>{{ day.date | moment('ddd, MMM Do') }}</h3>

                            <timeline class="mt-6" timeline-bg="#85a3c2">
                                <timeline-item
                                    v-for="time in day.times"
                                    bg-color="#85a3c2"
                                    line-color="#dbdde0"
                                    font-color="#37414a"
                                    :hollow="true"
                                    class="mt-5"
                                >
                                    <h4 class="mb-0">{{ time.time | moment('h:mm A') }}</h4>

                                    <div class="is-size-7 has-text-grey-dark is-italic">
                                        <section v-if="time.sessions.length == 1">Only {{time.sessions.length}} session available.</section>
                                        <section v-else>{{time.sessions.length}} concurrent sessions available.</section>
                                    </div>

                                    <b-button size="is-small" @click="time.choose = !time.choose" :disabled="!!registration.is_po_pending" class="mt-5">
                                        <i class="fas fa-list-timeline mr-2"></i><span v-if="!hasEnrollments(time)">Choose</span><span v-else>Change</span> Session
                                    </b-button>


                                    <div v-if="!time.choose && hasEnrollments(time)" class="mt-5">
                                        <div v-for="session in time.sessions" v-if="isEnrolled(session.id)" class="columns is-gapless pt-2 session">
                                            <div class="column is-narrow">
                                                <h6><i class="fad fa-screen-users ml-1 mr-4 has-text-primary"></i></h6>
                                            </div>
                                            <div class="column">
                                                <h6 class="mb-2">{{ session.title }}</h6>
                                                <div class="session-meta">
                                                    <span>{{ session.start_at | moment('dddd, MMMM Do, h:mm A') }} - {{ session.end_at | moment('h:mm A') }}</span>
                                                    <span v-if="session.presenter" class="is-hidden-mobile">, <i class="fad fa-person-chalkboard ml-1 fa-flip-horizontal"></i> {{ session.presenter }}</span>
                                                </div>
                                                <section v-if="session.information">
                                                    <hr style="margin: 1rem 0"/>
                                                    <div v-html="session.information"></div>
                                                </section>
                                            </div>
                                        </div>

                                    </div>
                                    <div v-if="time.choose" class="mt-5">
                                        <schedule-session-component
                                            v-for="session in time.sessions"
                                            :key="session.id"
                                            :session="session"
                                            :registration="registration"
                                            :state-registration="stateRegistration"
                                            :processing="processing"
                                            @schedule="tabIndex=0"
                                            @attend="(sess) => { attend(sess); time.choose = false; }"
                                            @remove="remove"
                                        ></schedule-session-component>
                                    </div>
                                </timeline-item>
                            </timeline>
                        </div>
                    </b-tab-item>

                    <b-tab-item>
                        <template #header><i class="fad fa-list-timeline mr-2"></i>Conference<span class="is-hidden-mobile">&nbsp;Schedule</span></template>

                        <b-message type="is-warning" class="mt-5" v-if="stateRegistration.isLocked">
                            <div class="columns is-gapless">
                                <div class="column is-narrow"><i class="fas fa-exclamation-triangle fa-fw mr-3 has-text-warning"></i></div>
                                <div class="column">
                                    <div class="is-size-6 has-text-weight-bold">Registration Locked</div>
                                    <div class="is-size-7-mt-3">You have completed all available applications and forms, so no further changes to your registration are available.</div>
                                </div>
                            </div>
                        </b-message>

                        <div v-for="day in sessionsByDay" class="mt-6">
                            <h3 class="is-hidden-mobile"><i class="fad fa-calendar-day mr-4 ml-5 has-text-primary"></i>{{ day.date | moment('dddd, MMMM Do, YYYY') }}</h3>
                            <h3 class="is-hidden-desktop"><i class="fad fa-calendar-day mr-4 ml-5 has-text-primary"></i>{{ day.date | moment('ddd, MMM Do') }}</h3>

                            <timeline class="mt-6" timeline-bg="#85a3c2">
                                <timeline-item
                                    v-for="time in day.times"
                                    bg-color="#85a3c2"
                                    line-color="#dbdde0"
                                    font-color="#37414a"
                                    :hollow="true"
                                    class="mt-5"
                                >
                                    <h4 class="mb-0">{{ time.time | moment('h:mm A') }}</h4>

                                    <div class="mt-5">
                                        <schedule-session-component
                                            v-for="session in time.sessions"
                                            :key="session.id"
                                            :session="session"
                                            :registration="registration"
                                            :state-registration="stateRegistration"
                                            :processing="processing"
                                            @schedule="tabIndex=0"
                                            @attend="attend"
                                            @remove="remove"
                                        ></schedule-session-component>
                                    </div>
                                </timeline-item>
                            </timeline>
                        </div>
                    </b-tab-item>


                </b-tabs>

                <div class="columns is-gapless mb-6 pt-5" v-else>
                    <div class="column is-narrow is-size-5"><i class="far fa-fw fa-info-circle mr-4"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold is-size-5">There are no live sessions available for this conference.</div>
                        <div class="mt-4 pr-6">
                            Live sessions may be added later if included with this conference, otherwise sessions may only be available as <strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong>.
                        </div>
                    </div>
                </div>
            </ui-card>

        </div>
    </div>
</template>

<script>
    import {client as http} from "../../http_client";

    import async from "async";

    import {Timeline, TimelineItem, TimelineTitle} from 'vue-cute-timeline';
    import 'vue-cute-timeline/dist/index.css';

    import Moment from "moment";
    import {extendMoment} from 'moment-range';
    import UiCard from "../../components/UiCard";
    import {mapGetters} from "vuex";
    import ScheduleSessionComponent from "./ScheduleSessionComponent";

    const moment = extendMoment(Moment);

    export default {
        components: {
            ScheduleSessionComponent,
            UiCard,
            Timeline,
            TimelineItem,
            TimelineTitle
        },
        props: ['registrationId'],

        data: function() {
            return {
                registration: {},
                sessionsByDay: {},
                sessionCount: 0,
                tabIndex: 0,
                processing: false
            };
        },
        computed: {
            ...mapGetters({
                stateRegistration: 'registration' // TODO just a temp fix
            })
        },
        methods: {
            loadRegistration: function(force) {
                http.get('app/registrations/' + this.registrationId, {force}).then(response => {

                    let sessions = response.data.conference.sessions;
                    let days = {};

                    // sort sessions by start_at
                    sessions.sort((a, b) => {
                        return moment(a.start_at).diff(moment(b.start_at));
                    });

                    // group sessions by day
                    for (let i = 0; i < sessions.length; i++) {
                        const session = sessions[i];

                        if (session.type !== 'on-demand') {
                            this.sessionCount++;
                            session.range = moment.range(session.start_at, session.end_at);
                            session.allowed_types = JSON.parse(session.allowed_types);
                            session.show_conflicts = false;

                            const day = moment(session.start_at).format('YYYY-MM-DD');
                            if (!days[day]) {
                                days[day] = {date: moment(session.start_at).startOf('day').toDate(), sessions: []};
                            }
                            days[day].sessions.push(session);
                        }
                    }

                    // group each day sessions by start_at time
                    for (let day in days) {
                        let sessions = days[day].sessions;
                        let times = {};
                        for (let i = 0; i < sessions.length; i++) {
                            const session = sessions[i];

                            if (session.type !== 'on-demand') {
                                const time = moment(session.start_at).format('h:mm A');
                                if (!times[time]) {
                                    times[time] = {time: moment(session.start_at).toDate(), sessions: []};
                                    times[time].choose = false;
                                }
                                times[time].sessions.push(session);
                            }
                        }
                        days[day].times = times;
                    }

                    this.sessionsByDay = days;
                    this.registration = response.data;
                });
            },
            attend: function(session) {
                if (!this.processing) {
                    this.processing = true;

                    async.series([
                        (cb) => {
                            let conflicts = this.conflicts(session);

                            async.each(conflicts, (conflict, ecb) => {
                                http.delete('app/registrations/' + this.registration.id + '/sessions/' + conflict.id, {force: true}).then(response => {
                                    const index = this.registration.reg_sessions_ids.indexOf(conflict.id);
                                    if (index > -1) {
                                        this.registration.reg_sessions_ids.splice(index, 1);
                                    }
                                    ecb();
                                });
                            }, cb);
                        },
                        (cb) => {
                            http.post('app/registrations/' + this.registration.id + '/sessions', {session_id: session.id}, {force: true}).then(response => {
                                this.registration.reg_sessions_ids.push(session.id);
                                cb();
                            });
                        }
                    ], err => {
                        session.show_conflicts = false;
                        this.processing = false;
                    });
                }
            },
            remove: function(session) {
                if (!this.processing) {
                    this.processing = true;
                    http.delete('app/registrations/' + this.registration.id + '/sessions/' + session.id, {force: true}).then(response => {
                        const index = this.registration.reg_sessions_ids.indexOf(session.id);
                        if (index > -1) {
                            this.registration.reg_sessions_ids.splice(index, 1);
                        }
                        this.processing = false;
                    });
                }
            },
            isEnrolled(sessionId) {
                return this.registration.reg_sessions_ids.includes(sessionId);
            },
            hasEnrollments(time) {
                for (let i = 0; i < time.sessions.length; i++) {
                    const session = time.sessions[i];
                    if (this.isEnrolled(session.id)) return true;
                }
            },
            isAllowed(session) {
                return session.allowed_types.includes(this.registration.reg_type_id);
            },
            hasAllowedSessions(day) {
                for (let time in day.times) {
                    for (let i = 0; i < day.times[time].sessions.length; i++) {
                        const session = day.times[time].sessions[i];
                        if (this.isAllowed(session)) return true;
                    }
                }
                return false;
            },
            conflicts(subjectSession) {
                let conflicts = [];

                if (this.registration.reg_sessions_ids.length && this.registration.conference) {
                    for (let i = 0; i < this.registration.conference.sessions.length; i++) {
                        const session = this.registration.conference.sessions[i];

                        if (session.id === subjectSession.id) continue;

                        if (this.isEnrolled(session.id) && subjectSession.range.overlaps(session.range)) {
                            conflicts.push(session);
                        }
                    }
                    return conflicts;
                } else {
                    return [];
                }
            }
        },
        mounted() {
            this.loadRegistration();
        }
    };
</script>

<style lang="scss" scoped>

    .session {
        border: 1px solid #e7e7e7;
        background-color: #fafafa;
        border-radius: 5px;
        padding: 1rem !important;

        .conflicts {
            background-color: white;
            border: 1px solid #dbdbdb;
            padding: 1rem;
            border-radius: 5px;
        }

        .session-meta {
            font-size: 0.85rem;
        }
    }
</style>

<style lang="scss">
    .schedule-tabs {

        .tabs {
            ul {
                margin-left: 22px;
            }

            a {
                @media (max-width: 768px) {
                    padding: 0 1rem 0 0 !important;
                }
            }
        }

        .tab-content {
            @media (max-width: 768px) {
                padding: 0 !important;
            }
        }
    }
</style>
